import { z } from "zod";

export const roleSchema = z.enum(["admin", "member", "owner"]);

export type Role = z.infer<typeof roleSchema>;

export const userOutputSchema = z.object({
    id: z.string(),
    name: z.string(),
    image: z.string().nullish(),
    email: z.string(),
});
export type UserOutput = z.infer<typeof userOutputSchema>;

/*
 * Per-user display preferences. Stored on `users` (not per organization), so they follow
 * the person across every team they belong to.
 *
 * Date formats are stored as the date-fns pattern they render with. The first entry of
 * each enum is the default, and matches what the app hardcoded before preferences
 * existed, so a user who never opens the settings sees no change.
 */
export const dateFormatSchema = z.enum([
    "MMM d, yyyy",
    "d MMM yyyy",
    "MM/dd/yyyy",
    "dd/MM/yyyy",
    "dd.MM.yyyy",
    "yyyy-MM-dd",
]);
export type DateFormat = z.infer<typeof dateFormatSchema>;

export const timeFormatSchema = z.enum(["12h", "24h"]);
export type TimeFormat = z.infer<typeof timeFormatSchema>;

export const weekStartSchema = z.enum(["sunday", "monday", "saturday"]);
export type WeekStart = z.infer<typeof weekStartSchema>;

export const DEFAULT_USER_PREFERENCES = {
    dateFormat: "MMM d, yyyy",
    timeFormat: "12h",
    weekStart: "sunday",
} as const satisfies UserPreferences;

// `.catch` keeps a read working if the database ever holds a value this build does not
// know (e.g. after a rollback): the user falls back to the default instead of an error.
export const userPreferencesSchema = z.object({
    dateFormat: dateFormatSchema.catch(DEFAULT_USER_PREFERENCES.dateFormat),
    timeFormat: timeFormatSchema.catch(DEFAULT_USER_PREFERENCES.timeFormat),
    weekStart: weekStartSchema.catch(DEFAULT_USER_PREFERENCES.weekStart),
});
export type UserPreferences = {
    dateFormat: DateFormat;
    timeFormat: TimeFormat;
    weekStart: WeekStart;
};

export const updateUserPreferencesSchema = z
    .object({
        dateFormat: dateFormatSchema,
        timeFormat: timeFormatSchema,
        weekStart: weekStartSchema,
    })
    .partial();
export type UpdateUserPreferences = z.infer<typeof updateUserPreferencesSchema>;
